<main class="-mt-16 flex w-full flex-col gap-[128px]">
  <!-- Hero Section -->
  <div class="relative flex h-[640px] w-full flex-col rounded-[48px]">
    <img
      ngSrc="assets/media/backgrounds/bg.svg"
      alt="bg"
      width="1216"
      height="640"
      priority
      class="block h-full w-full opacity-0 transition-opacity duration-700"
      (load)="imageLoaded.set(true)"
      [class.opacity-100]="imageLoaded()"/>

    <div
      class="absolute left-1/2 top-1/2 flex w-full -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-10">
      <div class="flex flex-col items-center gap-4 px-10">
        <div class="flex items-center gap-1 rounded-md border border-border border-gray-200 p-1">
          <span [inlineSVG]="'assets/media/icons/bright-star.svg'" class="h-[18px] w-[18px] text-gray-500"></span>
          <span class="text-center text-sm text-secondary-foreground">Empowering Digital Innovation</span>
        </div>

        <h1 class="text-center text-5xl tracking-tighter">Building Digital Products That Drive Growth</h1>
        <p class="max-w-[592px] text-center text-lg text-secondary-foreground">
          From concept and design to development and testing — SAPEC is your full-cycle software partner for fast,
          scalable, and
          high-quality web and mobile applications
        </p>
      </div>
      <div class="flex w-full max-w-[300px] gap-4">
        <button hlmBtn class="h-12 w-full rounded-xl bg-primary !text-base text-white" (click)="redirectToHireUs()">
          Contact Us
        </button>
        <button
          hlmBtn
          (click)="redirectToServices()"
          class="h-12 w-full rounded-xl border border-primary bg-background text-base text-primary hover:bg-gray-100 dark:bg-background hover:dark:bg-gray-50">
          Our Services
        </button>
      </div>
    </div>
  </div>

  <!-- Technological Overview Section -->
  <div class="flex w-full flex-col gap-16">
    <app-section-header
      title="What We Deliver"
      description="At SAPEC, we specialize in turning complex ideas into elegant and powerful digital solutions."/>

    <div class="flex w-full flex-col gap-20 lg:gap-10">
      @for (item of technologiesDetails; track item; let i = $index) {
        <div
          [ngClass]="[i % 2 === 0 ? 'w-full max-w-[624px] self-center lg:self-start' : 'w-full max-w-[624px] self-center lg:self-end']">
          <app-compound-card
            [showButton]="true"
            [title]="item.title"
            [subtitle]="item.subtitle"
            [features]="item.badges"
            [suffixInlineSvgIcon]="item.iconLink"
            [iconBgColor]="item.bgColor"
            [containerClassName]="'relative transform ' + (i % 2 === 0 ? 'lg:animate-custom-float-odd' : 'lg:animate-custom-float-even')"
            [ngStyle]="{ '--float-duration': item.floatDuration }"
            [alwaysShowShadow]="true"
            subtitleClassName="text-xl"
            titleClassName="text-[28px]"
            sectionClassName="p-8 bg-gray-100 transition-all"/>
        </div>
      }
    </div>
  </div>

  <!-- Work With Us Section -->
  <div class="flex flex-col gap-y-4 lg:grid lg:grid-cols-12 lg:grid-rows-[0.5fr_0.5fr_1fr]">
    <div class="col-span-12 col-start-1 row-span-2 row-start-1">
      <app-info-card
        title="Want to work with us?"
        subtitle="Let’s build something great — together"
        subtitleClassName="text-2xl"
        titleClassName="text-3xl"
        sectionClassName="w-full p-8 bg-gray-100"
        [showButton]="true"
        buttonText="Schedule a Consultation"
        (buttonClick)="redirectToHireUs()"/>
    </div>

    <div
      class="col-span-8 col-start-5 row-span-2 row-start-2 flex flex-col justify-center gap-4 md:flex-row lg:mr-8 lg:mt-1 lg:justify-start lg:gap-10">
      @for (item of workWithUsItems; track item; let i = $index) {
        <app-info-card
          [title]="item.title"
          [description]="item.description"
          [containerClassName]="i % 2 === 0 ? 'lg:-rotate-[3deg] lg:max-w-[384px]' : 'lg:rotate-[3deg] lg:max-w-[384px]'"
          sectionClassName="bg-primary-100 dark:bg-primary-800 p-8"
          titleClassName="text-[28px]"
          descriptionClassName="text-xl"/>
      }
    </div>
  </div>

  <!-- Particularities Section -->
  <div class="flex flex-col gap-8">
    <app-section-header
      title="What Makes Us Different"
      description="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc vulputate libero et velit interdum, ac aliquet odio mattis."/>

    <div class="grid auto-rows-[486px] grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      @for (item of particularities; track item; let i = $index) {
        <div class="w-full">
          <app-image-card
            [icon]="item.icon"
            [sectionClassName]="i === 0 ? 'bg-primary-100 dark:bg-primary-800 w-full justify-center' : 'bg-gray-100 w-full justify-center'"
            [title]="item.title"
            [description]="item?.description"
            titleClassName="text-center text-xl w-full"
            descriptionClassName="text-center text-lg"
            containerClassName="group w-full h-full items-center"/>
        </div>
      }
    </div>
  </div>

  <!-- Skills Section -->
  <div class="flex flex-col gap-8">
    <app-section-header
      title="From concept to code — we cover the full project lifecycle"
      description="At SAPEC, we provide end-to-end development services for web and mobile platforms. Whether you're launching a new product or
       scaling an existing one, our team handles every technical and creative challenge with precision and care"
      badge="Our Skills"/>

    <div class="grid auto-rows-auto grid-cols-1 grid-rows-none gap-14 lg:grid-cols-2">
      @for (item of skills; track item) {
        <app-info-card
          [containerClassName]="item.rotateDeg"
          [sectionClassName]="'p-8 ' + item.bgColor + '-100 dark:' + item.bgColor + '-600'"
          [title]="item.title"
          [subtitle]="item.subtitle"
          [highlightTitle]="item.highlight"
          [description]="item.description"
          [listItems]="item.features"
          [showListItemIcon]="true"
          [isMonochromeListBullet]="true"
          titleClassName="text-3xl"
          subtitleClassName="text-2xl text-secondary-foreground"
          highlightTitleClassName="text-xl font-medium"
          descriptionClassName="text-lg text-secondary-foreground pt-2.5"
          listContainerClassName="gap-6"
          listItemsClassName="text-lg text-foreground"/>
      }
    </div>
  </div>

  <!-- Contact Us Section -->
  <div class="flex flex-col gap-16">
    <app-section-header
      title="Let’s build an awesome project together!"
      description="Ready to bring your ideas to life? Let's collaborate to create exceptional solutions that meet your business needs."/>

    <div class="grid grid-cols-1 justify-between gap-8 md:grid-cols-2 lg:grid-cols-4 lg:flex-row lg:gap-8">
      @for (item of contactInfoItems; track item) {
        <div class="flex w-full flex-col">
          <app-info-card
            [prefixIcon]="item.icon"
            [title]="item.title"
            [listItems]="item.data"
            titleClassName="text-xl text-center font-semibold lg:text-start"
            sectionClassName="bg-gray-100"
            listItemsClassName="text-center"/>
        </div>
      }
    </div>
  </div>
</main>
